import { Collapse } from '@chakra-ui/react'
import React from 'react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import './Sidebar.css'

interface INavbarItem {
  title: string
  icon?: string
  path: string
  roles: string[]
}

interface INavbarGroup {
  title: string
  icon: string
  items: INavbarItem[]
  roles: string[]
}

const navbarItems: (INavbarGroup | INavbarItem)[] = [
  {
    title: 'Dashboard',
    icon: 'lni-grid-alt',
    path: '/home',
    roles: ['admin', 'relacoes-humanas', 'geral']
  },
  {
    title: 'Comercial',
    icon: 'lni-briefcase',
    items: [
      {
        title: 'Base de Leads',
        path: '/form-leads',
        roles: ['admin', 'comercial']
      },
      {
        title: 'Consultores Comerciais',
        path: '/consultores-comerciais',
        roles: ['admin']
      },
      {
        title: 'Feriados e Plantões',
        path: '/feriados-e-plantoes',
        roles: ['admin', 'comercial']
      },
      {
        title: 'Black List',
        icon: 'lni-ban',
        path: '/black-list',
        roles: ['admin']
      }
    ],
    roles: ['admin', 'comercial']
  },
  {
    title: 'Experiência do Aluno',
    icon: 'lni-graduation',
    items: [
      {
        title: 'Solicitações',
        path: '/solicitacoes',
        roles: ['admin', 'relacoes-humanas', 'geral']
      },
      {
        title: 'Video Quiz',
        path: '/video-quizes',
        roles: ['admin', 'geral']
      }
    ],
    roles: ['admin', 'relacoes-humanas', 'geral']
  },
  {
    title: 'Pedagógico',
    icon: 'lni-book',
    items: [
      {
        title: 'Entrega de Trabalhos',
        icon: 'lni-files',
        path: '/entrega-de-trabalhos',
        roles: ['admin', 'geral']
      }
    ],
    roles: ['admin', 'geral']
  },
  {
    title: 'RH',
    icon: 'lni-users',
    items: [
      {
        title: 'Acelera Magenta',
        icon: 'lni-ruler-pencil',
        path: '/acelera-magenta',
        roles: ['admin', 'relacoes-humanas']
      },
      {
        title: 'Cadastro de Vagas',
        icon: 'lni-briefcase',
        path: '/job-positions',
        roles: ['admin', 'relacoes-humanas']
      },
      {
        title: 'Banco de Talentos',
        icon: 'lni-network',
        path: '/job-applications',
        roles: ['admin', 'relacoes-humanas']
      }
    ],
    roles: ['admin', 'relacoes-humanas']
  },
  {
    title: 'Marketing',
    icon: 'lni-bullhorn',
    items: [
      {
        title: 'Encurtador de URL',
        icon: 'lni-link',
        path: '/url-shortener',
        roles: ['admin', 'relacoes-humanas', 'geral', 'comercial'] // disponível para todos
      }
    ],
    roles: ['admin', 'relacoes-humanas', 'geral', 'comercial']
  },
  {
    title: 'Website',
    icon: 'lni-world',
    items: [
      {
        title: 'Banners',
        icon: 'lni-ruler-pencil',
        path: '/website-banners',
        roles: ['admin']
      },
      {
        title: 'Black List',
        icon: 'lni-ban',
        path: '/black-list',
        roles: ['admin']
      },
      {
        title: 'Blog Posts',
        icon: 'lni-write',
        path: '/blog-posts',
        roles: ['admin']
      },
      {
        title: 'Cursos',
        icon: 'lni-bookmark',
        path: '/courses',
        roles: ['admin']
      },
      {
        title: 'Professores',
        icon: 'lni-graduation',
        path: '/teachers',
        roles: ['admin']
      },
      {
        title: 'Depoimentos',
        icon: 'lni-pencil-alt',
        path: '/testimonials',
        roles: ['admin']
      }
    ],
    roles: ['admin']
  },
  {
    title: 'Administração',
    icon: 'lni-cog',
    items: [
      {
        title: 'Usuários',
        icon: 'lni-users',
        path: '/users',
        roles: ['admin']
      }
    ],
    roles: ['admin']
  }
]

function hasRole(routeRoles: string[], userRoles: string[]): boolean {
  const set = new Set(routeRoles)

  for (let i = 0; i < userRoles.length; i++) {
    if (set.has(userRoles[i])) {
      return true
    }
  }

  return false
}

export default function Sidebar() {
  const [sidebarActive, setSidebarActive] = useState(false)
  const [activeItem, setActiveItem] = useState<number | null>(null)
  const [userRoles, setUserRoles] = useState<string[]>([])
  const [sidebarItemOpen, setSidebarItemOpen] = useState<number | null>(null)

  useEffect(() => {
    const currentPath = window.location.pathname

    setActiveItem(
      navbarItems.findIndex((item) => {
        if ('items' in item) {
          return item.items.some((subItem) => subItem.path === currentPath)
        }

        return item.path.includes(currentPath)
      })
    )

    const user = localStorage.getItem('user')
    setUserRoles(user ? JSON.parse(user).roles : [])
  }, [])

  return (
    <div
      className={`sidebar min-vh-100 d-none d-md-block ${sidebarActive ? 'open' : ''}`}
      onMouseEnter={() => {
        setSidebarActive(true)
      }}
      onMouseLeave={() => {
        setSidebarActive(false)
      }}
    >
      <div className="text-center mt-2 mb-4">
        <img
          src={sidebarActive ? '/img/logo.png' : '/img/logo-mini.png'}
          className={`d-inline-block ${sidebarActive ? 'w-75' : 'w-50'} mt-3`}
        />
      </div>
      {navbarItems
        .filter((item) => hasRole(item.roles, userRoles))
        .map((item, index) => {
          if ('path' in item) {
            return (
              <Link
                to={item.path}
                key={`navbar-item-${index}`}
                className={`item text-decoration-none ${activeItem == index ? 'active' : ''}`}
                onClick={() => {
                  setActiveItem(index)
                }}
              >
                <i className={`lni ${item.icon}`}></i>
                <span className={sidebarActive ? 'd-inline' : 'd-none'}>{item.title}</span>
              </Link>
            )
          }

          return (
            <React.Fragment key={`group-${index}`}>
              <div
                className={`item ${
                  item.items.some((subItem) => subItem.path === window.location.pathname) ? 'active' : ''
                }`}
                onClick={() => {
                  setSidebarItemOpen(sidebarItemOpen === index ? null : index)
                  setActiveItem(index)
                }}
              >
                <i className={`lni ${item.icon}`}></i>
                <span className={sidebarActive ? 'd-inline' : 'd-none'}>{item.title}</span>
                <i className={`lni lni-chevron-down ms-auto ${sidebarItemOpen === index ? 'open' : ''}`}></i>
              </div>
              <Collapse in={sidebarItemOpen === index && sidebarActive} animateOpacity>
                {item.items
                  .filter((item) => hasRole(item.roles, userRoles))
                  .map((subItem, subIndex) => {
                    return (
                      <Link
                        to={subItem.path}
                        key={`subitem-${index}-${subIndex}`}
                        className={`subitem text-decoration-none ps-5 ${
                          activeItem === index && subItem.path === window.location.pathname ? 'active' : ''
                        }`}
                        onClick={() => {
                          setActiveItem(index)
                        }}
                      >
                        <span className={sidebarActive ? 'd-inline' : 'd-none'}>{subItem.title}</span>
                      </Link>
                    )
                  })}
              </Collapse>
            </React.Fragment>
          )
        })}
    </div>
  )
}
